/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type RemoveNodesFromGroupVariables = {
  nodeGroupId: string;
  nodeIds: string[];
};

export type RemoveNodesFromGroupMutationConfig = {
  onSuccess?: (variables: RemoveNodesFromGroupVariables) => void;
  onError?: (error: unknown, variables: RemoveNodesFromGroupVariables) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for removing nodes from an existing node group.
 *
 * @param config.onSuccess - Called after nodes are successfully removed
 * @param config.onError - Called if removing nodes fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for removing nodes from a group
 */
export function useRemoveNodesFromGroupMutation(config: RemoveNodesFromGroupMutationConfig) {
  const queryClient = useQueryClient();
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, mutateAsync, isError, error, isPending } = useMutation<void, ApiError, RemoveNodesFromGroupVariables>(
    {
      mutationFn: ({ nodeGroupId, nodeIds }: RemoveNodesFromGroupVariables) => {
        return mgmtPlaneAPIClient.nodeGroups.removeNodesFromGroup({
          tenantId: tenantId!,
          nodeGroupId,
          requestBody: { nodeIds },
        });
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['nodeGroups', tenantId] });
        queryClient.invalidateQueries({ queryKey: ['nodeGroups', tenantId, variables.nodeGroupId] });
        queryClient.invalidateQueries({ queryKey: ['nodes'] });
        config?.onSuccess?.(variables);
      },
      onError: config?.onError,
      onSettled: config?.onSettled,
    },
  );

  return { mutate, mutateAsync, isError, error, isPending };
}
